import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, BarChart3, Building2, CalendarClock, CheckCircle2, Clock3, Download, Save } from 'lucide-react'
import {
  exportPortfolio,
  getBenchmark,
  getPortfolio,
  getSchedule,
  triggerPortfolioExportDownload,
  updateSchedule,
  type AuditSchedule,
  type BenchmarkEntry,
  type PortfolioEntry,
} from '../../lib/services/chartAudit'

const CADENCE_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'custom', label: 'Custom' },
] as const

const SORT_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'name', label: 'Name' },
  { value: 'findings', label: 'Findings' },
  { value: 'overdue', label: 'Overdue' },
] as const

type SortMode = (typeof SORT_OPTIONS)[number]['value']

export function ChartAuditPortfolioPage() {
  const [portfolio, setPortfolio] = useState<PortfolioEntry[]>([])
  const [selectedFacilityId, setSelectedFacilityId] = useState('')
  const [sortMode, setSortMode] = useState<SortMode>('date')
  const [benchmark, setBenchmark] = useState<Record<string, BenchmarkEntry>>({})
  const [schedule, setSchedule] = useState<AuditSchedule | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [exportMessage, setExportMessage] = useState<string | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadPortfolio() {
      try {
        setLoading(true)
        const rows = await getPortfolio()
        if (cancelled) return
        setPortfolio(rows)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load portfolio')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadPortfolio()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (portfolio.length === 0) return
    const hasSelectedFacility = portfolio.some((entry) => entry.facility_id === selectedFacilityId)
    if (!hasSelectedFacility) {
      setSelectedFacilityId(portfolio[0].facility_id)
    }
  }, [portfolio, selectedFacilityId])

  useEffect(() => {
    if (!selectedFacilityId) return
    let cancelled = false

    async function loadFacilityPhase4State() {
      try {
        const [benchmarkData, scheduleData] = await Promise.all([
          getBenchmark(selectedFacilityId),
          getSchedule(selectedFacilityId),
        ])
        if (cancelled) return
        setBenchmark(benchmarkData)
        setSchedule(scheduleData)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load portfolio details')
      }
    }

    loadFacilityPhase4State()
    return () => { cancelled = true }
  }, [selectedFacilityId])

  const sortedPortfolio = [...portfolio].sort((left, right) => {
    if (sortMode === 'name') {
      return left.facility_name.localeCompare(right.facility_name)
    }

    if (sortMode === 'findings') {
      return right.open_findings - left.open_findings || left.facility_name.localeCompare(right.facility_name)
    }

    if (sortMode === 'overdue') {
      return Number(right.overdue) - Number(left.overdue)
        || (right.days_since_audit ?? -1) - (left.days_since_audit ?? -1)
        || left.facility_name.localeCompare(right.facility_name)
    }

    const leftTime = left.last_audit_date ? new Date(left.last_audit_date).getTime() : 0
    const rightTime = right.last_audit_date ? new Date(right.last_audit_date).getTime() : 0
    return rightTime - leftTime || left.facility_name.localeCompare(right.facility_name)
  })

  const selectedFacility = sortedPortfolio.find((entry) => entry.facility_id === selectedFacilityId) ?? null
  const benchmarkRows = Object.entries(benchmark).slice(0, 6)
  const overdueFacilities = portfolio.filter((entry) => entry.overdue).length
  const totalOpenFindings = portfolio.reduce((total, entry) => total + entry.open_findings, 0)

  async function handleSaveSchedule() {
    if (!selectedFacilityId || !schedule) return
    setSaving(true)
    setMessage(null)
    setError(null)
    try {
      await updateSchedule(selectedFacilityId, schedule)
      setPortfolio((current) => current.map((entry) => (
        entry.facility_id === selectedFacilityId
          ? { ...entry, schedule: { ...(entry.schedule ?? {}), ...schedule } }
          : entry
      )))
      setMessage('Schedule updated.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update schedule')
    } finally {
      setSaving(false)
    }
  }

  async function handleExportPortfolio() {
    setExporting(true)
    setExportMessage(null)
    setExportError(null)
    try {
      const result = await exportPortfolio()
      const filename = triggerPortfolioExportDownload(result)
      setExportMessage(`Downloaded ${filename} (redacted export).`)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Failed to export portfolio')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="v11-workspace-header flex items-center gap-4">
        <Link to="/chart-audit" className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
          <ArrowLeft size={18} />
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Chart Audit Portfolio</h1>
          <p className="mt-1 text-sm text-slate-500">Cross-facility overview, anonymized benchmarking, and cadence management.</p>
        </div>
        {!loading ? (
          <button
            type="button"
            onClick={handleExportPortfolio}
            disabled={exporting}
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            title="Download redacted portfolio JSON (admin/editor/superadmin)"
          >
            <Download size={16} />
            {exporting ? 'Exporting…' : 'Export redacted'}
          </button>
        ) : null}
      </div>
      {exportError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">
          {exportError}
        </div>
      ) : null}
      {exportMessage ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800" role="status">
          {exportMessage}
        </div>
      ) : null}

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-500">Loading portfolio...</div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      ) : portfolio.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
          No multi-facility chart-audit portfolio data is available yet.
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="v11-kpi-card">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Facilities</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{portfolio.length}</p>
            </div>
            <div className={`v11-kpi-card ${overdueFacilities > 0 ? 'v11-kpi-accent-rose' : ''}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Overdue (&gt;90 days)</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{overdueFacilities}</p>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-gold">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Open Findings</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{totalOpenFindings}</p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.4fr,1fr]">
            <div className="v11-section p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Facility Portfolio</h2>
                  <p className="mt-1 text-xs text-slate-500">Latest completed batch, open findings, and audit cadence.</p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{portfolio.length} facilities</span>
              </div>
              <div className="mt-4 flex gap-2">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSortMode(option.value)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${sortMode === option.value ? 'bg-[var(--brand-primary)] text-white' : 'border border-slate-200 bg-white text-slate-600'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <div className="mt-4 space-y-3">
                {sortedPortfolio.map((entry) => (
                  <button
                    key={entry.facility_id}
                    type="button"
                    onClick={() => setSelectedFacilityId(entry.facility_id)}
                    className={`w-full rounded-xl border p-4 text-left transition ${selectedFacilityId === entry.facility_id ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/5' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Building2 size={16} className="text-slate-400" />
                          <p className="font-semibold text-slate-900">{entry.facility_name}</p>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Last audit: {entry.last_audit_date ? new Date(entry.last_audit_date).toLocaleDateString() : 'Never'}
                          {entry.days_since_audit !== null ? ` • ${entry.days_since_audit} days ago` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`rounded-full px-2.5 py-0.5 font-semibold ${entry.overdue ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                          {entry.overdue ? 'Overdue' : 'Current'}
                        </span>
                        <span className="rounded-full bg-amber-50 px-2.5 py-0.5 font-semibold text-amber-700">
                          {entry.open_findings} open
                        </span>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-3">
                      <span>Cadence: {entry.schedule?.cadence ?? 'Not set'}</span>
                      <span>Next due: {entry.schedule?.next_due_date ?? 'Not set'}</span>
                      <span>Min cadence: {entry.schedule?.minimum_cadence ?? 'None'}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="v11-section p-5">
                <div className="flex items-center gap-2">
                  <BarChart3 size={18} className="text-slate-400" />
                  <h2 className="text-lg font-bold text-slate-900">Benchmark Snapshot</h2>
                </div>
                <p className="mt-1 text-xs text-slate-500">Aggregate percentile bands only. No peer facility identities are exposed.</p>
                    {selectedFacility ? (
                  <div className="mt-4 space-y-2">
                    {benchmarkRows.length === 0 ? (
                      <p className="text-sm text-slate-500">No benchmark data available yet for {selectedFacility.facility_name}.</p>
                    ) : benchmarkRows.map(([ruleId, entry]) => (
                      <div key={ruleId} className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{ruleId}</span>
                          <span>Facility {entry.facility_rate ?? '—'}%</span>
                        </div>
                        <div className="mt-1 flex gap-3 text-slate-500">
                          <span>P25 {entry.p25}%</span>
                          <span>P50 {entry.p50}%</span>
                          <span>P75 {entry.p75}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="v11-section p-5">
                <div className="flex items-center gap-2">
                  <CalendarClock size={18} className="text-slate-400" />
                  <h2 className="text-lg font-bold text-slate-900">Audit Schedule</h2>
                </div>
                {selectedFacility && schedule ? (
                  <div className="mt-4 space-y-3">
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Cadence
                      <select
                        value={schedule.cadence}
                        onChange={(event) => setSchedule({ ...schedule, cadence: event.target.value as AuditSchedule['cadence'] })}
                        className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
                      >
                        {CADENCE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>

                    {schedule.cadence === 'custom' && (
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Custom Interval (days)
                        <input
                          type="number"
                          min={1}
                          value={schedule.custom_interval_days ?? ''}
                          onChange={(event) => setSchedule({ ...schedule, custom_interval_days: event.target.value ? Number(event.target.value) : null })}
                          className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                        />
                      </label>
                    )}

                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Next Due Date
                      <input
                        type="date"
                        value={schedule.next_due_date ?? ''}
                        onChange={(event) => setSchedule({ ...schedule, next_due_date: event.target.value || null })}
                        className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                      />
                    </label>

                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Reminder Lead Time (days)
                      <input
                        type="number"
                        min={0}
                        max={90}
                        value={schedule.reminder_days_before}
                        onChange={(event) => setSchedule({ ...schedule, reminder_days_before: Number(event.target.value) })}
                        className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                      />
                    </label>

                    <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      <div className="flex items-center gap-2">
                        <Clock3 size={14} className="text-slate-400" />
                        Last completed audit: {schedule.last_completed_date ?? 'Not recorded'}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleSaveSchedule}
                      disabled={saving}
                      className="inline-flex items-center gap-2 rounded-lg bg-[var(--brand-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50"
                    >
                      <Save size={16} />
                      {saving ? 'Saving...' : 'Save Schedule'}
                    </button>

                    {message ? <p className="text-sm text-green-700">{message}</p> : null}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-slate-500">Select a facility to manage its chart-audit cadence.</p>
                )}
              </div>
            </div>
          </div>

          {selectedFacility?.last_batch_score ? (
            <div className="v11-section p-5">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={18} className="text-slate-400" />
                <h2 className="text-lg font-bold text-slate-900">Latest Score Distribution</h2>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3 text-sm">
                <div className="rounded-lg bg-green-50 px-4 py-3 text-green-800">
                  <p className="text-xs font-semibold uppercase tracking-wider">Well Maintained</p>
                  <p className="mt-1 text-2xl font-bold">{selectedFacility.last_batch_score['1'] ?? 0}</p>
                </div>
                <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
                  <p className="text-xs font-semibold uppercase tracking-wider">Needs Improvement</p>
                  <p className="mt-1 text-2xl font-bold">{selectedFacility.last_batch_score['2'] ?? 0}</p>
                </div>
                <div className="rounded-lg bg-red-50 px-4 py-3 text-red-800">
                  <p className="text-xs font-semibold uppercase tracking-wider">Not Maintained</p>
                  <p className="mt-1 text-2xl font-bold">{selectedFacility.last_batch_score['3'] ?? 0}</p>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
